import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Send,
  Loader2,
  AlertCircle,
  Bot,
  User as UserIcon,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  Download,
  Search,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import QueryHintChips from './QueryHintChips';
import BpmTenantApprovalConfigPanel, {
  getBpmTenantConfigTabs,
} from './BpmTenantApprovalConfigPanel';
import BpmTaskDetailPanel from './BpmTaskDetailPanel';
import ApprovalCopilotLanding from './ApprovalCopilotLanding';
import { buildFallbackTaskConfiguration } from './bpmTaskConfigFallback';
import BpmConfiguredApprovalsList from './BpmConfiguredApprovalsList';
import { resolveSourceBadgeLabel } from './BpmTaskConfigOverview';
import {
  listApprovalDrafts,
  saveApprovalDraft,
  updateApprovalDraft,
  compareApprovalDraft,
  formatDraftApiError,
} from './approvalDraftApi';
import {
  filterTenantConfigForTask,
  designFromTenantRules,
  designFromInferredRouting,
  taskCategoryToTransactionCategory,
  slugTaskRuleId,
} from './bpmTaskTenantMatch';
import { buildConditionDescription } from './BpmConditionEditor';

const API = axios.create({
  baseURL: getApiBase(),
  timeout: 60000,
  withCredentials: true,
});

const FEATURE_CONFIG = {
  report: {
    label: 'Report Copilot',
    placeholder: 'Ask about a report or subject area…',
    system:
      'You are the Report Copilot inside an Oracle HCM tool. Help the user find or build the right ' +
      'BI Publisher or OTBI report. When you have enough info, propose a report and parameters in ' +
      'concise, friendly prose. If anything is ambiguous, ask one clarifying question. Never invent ' +
      'subject areas or columns you have not been shown.',
    starters: [
      'List all active employees in the US legal employer.',
      'Show overtime hours by department for the last pay period.',
      'Which workers are missing a national identifier?',
    ],
  },
  bpm: {
    label: 'Approval Co pilot',
    placeholder: '',
    system:
      'You are the Approval Co pilot for Oracle HCM. Given a plain-English approval policy, ' +
      'propose a structured rule (transaction type, conditions, approver path). Ask one clarifying ' +
      'question if the approver target is ambiguous. Always include a brief plain-English summary ' +
      'before the structured rule.',
    starters: [],
  },
  security: {
    label: 'Security Console Copilot',
    placeholder: 'Why can\'t this user see…?',
    system:
      'You are the Security Console Copilot for Oracle HCM. The admin asks why a user cannot see a ' +
      'page or data. Walk through the security model step by step: function privilege → role grants ' +
      'this privilege → data security policy → Area of Responsibility / hierarchy scope. State your ' +
      'best root cause and the smallest concrete fix.',
    starters: [
      "Why can't Jane Doe see the Compensation Workbench for her team?",
      "Why does Mark Smith see employees from another business unit?",
      "Which role grants access to the Talent Review meeting page?",
    ],
  },
  general: {
    label: 'AI Copilot',
    placeholder: 'Ask anything about your Oracle HCM tool…',
    system: 'You are a helpful assistant inside an Oracle HCM tool. Be concise and concrete.',
    starters: [],
  },
};

function newConversationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatCost(usd) {
  if (!usd || usd < 0.0001) return null;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function formatCopilotHttpError(err, fallback, { fusionBaseUrl } = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 404) {
    const fusion = fusionBaseUrl ? String(fusionBaseUrl).replace(/\/+$/, '') : '';
    return (
      data?.error ||
      (fusion
        ? `Configuration API is unavailable — showing SQL and Fusion sections from cached tenant data. Open Transaction Console on your pod for full IF/THEN rules.`
        : 'Configuration API is unavailable — connect your Fusion pod under Connections, or restart the API server.')
    );
  }
  if (status === 403) {
    return data?.error || 'AI Copilot is not enabled for your user.';
  }
  if (status === 503) {
    return data?.error || 'Service unavailable — check HCM connection or server logs.';
  }
  return data?.error || err?.message || fallback;
}

function formatBpmNlApiFailure(data) {
  if (!data || typeof data !== 'object') return 'AI did not return a valid approval rule.';
  const parts = [];
  if (data.error) parts.push(String(data.error));
  if (Array.isArray(data.lint?.errors)) {
    for (const e of data.lint.errors) {
      if (e && !parts.includes(e)) parts.push(String(e));
    }
  }
  if (data.devContextWarning) parts.push(String(data.devContextWarning));
  if (data.provider === 'mock' && String(data.error || '').includes('JSON')) {
    parts.push(
      'Mock LLM is active — pull latest server code and restart, or configure OCI_* env vars.'
    );
  }
  const raw = data.rawText ? String(data.rawText).trim() : '';
  if (raw) {
    const preview = raw.length > 240 ? `${raw.slice(0, 240)}…` : raw;
    parts.push(`Model output: ${preview}`);
  }
  return parts.filter(Boolean).join(' — ') || 'AI did not return a valid approval rule.';
}

function MessageBubble({ role, content, meta, onVerdict }) {
  const isUser = role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
        isUser
          ? 'bg-cyan-700 text-white'
          : 'bg-white border border-slate-200 text-slate-800 shadow-sm'
      }`}>
        <div className="whitespace-pre-wrap break-words">{content}</div>
        {!isUser && meta && (
          <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
            {meta.model && <span>{meta.model}</span>}
            {meta.latencyMs != null && <span>{meta.latencyMs} ms</span>}
            {meta.tokens && (
              <span>
                {meta.tokens.input}↑ / {meta.tokens.output}↓ tok
              </span>
            )}
            {formatCost(meta.costUsd) && <span>{formatCost(meta.costUsd)}</span>}
            {meta.interactionId && (
              <span className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  className="rounded p-1 hover:bg-slate-100 hover:text-emerald-700"
                  title="Helpful"
                  onClick={() => onVerdict?.(meta.interactionId, 'accepted')}
                  disabled={meta.verdict === 'accepted'}
                >
                  <ThumbsUp className={`h-3.5 w-3.5 ${meta.verdict === 'accepted' ? 'text-emerald-600' : ''}`} />
                </button>
                <button
                  type="button"
                  className="rounded p-1 hover:bg-slate-100 hover:text-rose-700"
                  title="Not helpful"
                  onClick={() => onVerdict?.(meta.interactionId, 'rejected')}
                  disabled={meta.verdict === 'rejected'}
                >
                  <ThumbsDown className={`h-3.5 w-3.5 ${meta.verdict === 'rejected' ? 'text-rose-600' : ''}`} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
      {isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
          <UserIcon className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}

/**
 * CopilotPanel — generic chat shell for Report / BPM / Security copilots.
 *
 * Props:
 *  - feature: 'report' | 'bpm' | 'security' | 'general' (default 'general')
 *  - title?: override panel title
 *  - subtitle?: short description shown under title
 *  - systemPrompt?: override the feature's default system prompt
 *  - context?: opaque JSON sent with each request for the audit log (RAG chunks etc.)
 */
function normalizeTaskNameKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function dedupeBpmTasks(tasks) {
  const map = new Map();
  for (const t of tasks || []) {
    const key = normalizeTaskNameKey(t.name);
    if (!key) continue;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, t);
      continue;
    }
    map.set(key, {
      ...prev,
      confirmedOnTenant: !!(prev.confirmedOnTenant || t.confirmedOnTenant),
      code: prev.code || t.code || null,
    });
  }
  return [...map.values()];
}

export default function CopilotPanel({
  feature = 'general',
  title,
  subtitle,
  systemPrompt,
  context = null,
  handoff = null,
  onHandoffConsumed,
  bpmLayout = 'landing',
  onOpenApprovalDesigner,
  onOpenConnections,
  onHandoffToCopilot,
}) {
  const config = FEATURE_CONFIG[feature] || FEATURE_CONFIG.general;

  const [messages, setMessages] = useState([]); // { role, content, meta? }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(() => newConversationId());
  const [providerStatus, setProviderStatus] = useState(null);
  const [apiHealth, setApiHealth] = useState(null);
  const [apiHealthBanner, setApiHealthBanner] = useState('');
  const scrollRef = useRef(null);

  const [bpmHcmConn, setBpmHcmConn] = useState(null);
  const [bpmStructuredPolicy, setBpmStructuredPolicy] = useState('');
  const [bpmDesignResult, setBpmDesignResult] = useState(null);
  const [bpmDesignLoading, setBpmDesignLoading] = useState(false);
  const [bpmNlRuleLoading, setBpmNlRuleLoading] = useState(false);
  const [bpmGenSeconds, setBpmGenSeconds] = useState(0);
  const [bpmNlOk, setBpmNlOk] = useState(false);
  const bpmDesignerRef = useRef(null);
  const [bpmNotice, setBpmNotice] = useState('');
  const [bpmTasksData, setBpmTasksData] = useState(null);
  const [bpmTasksLoading, setBpmTasksLoading] = useState(false);
  const [bpmTasksErr, setBpmTasksErr] = useState('');
  const [bpmTaskSearch, setBpmTaskSearch] = useState('');
  const [bpmTaskCategory, setBpmTaskCategory] = useState('all');
  const [bpmApprovalConfig, setBpmApprovalConfig] = useState(null);
  const [bpmApprovalConfigLoading, setBpmApprovalConfigLoading] = useState(false);
  const [bpmApprovalConfigErr, setBpmApprovalConfigErr] = useState('');
  const [bpmConfigSection, setBpmConfigSection] = useState('inferred');
  const [bpmPushConfig, setBpmPushConfig] = useState(null);
  const [bpmPushTargets, setBpmPushTargets] = useState([]);
  const [bpmPushTargetId, setBpmPushTargetId] = useState('');
  const [bpmPushCategory, setBpmPushCategory] = useState('absence');
  const [bpmPushResult, setBpmPushResult] = useState(null);
  const [bpmPushLoading, setBpmPushLoading] = useState(false);
  const [bpmDesignExpandSignal, setBpmDesignExpandSignal] = useState(0);
  const [bpmDesignPacks, setBpmDesignPacks] = useState([]);
  const [bpmActivePackId, setBpmActivePackId] = useState('');
  const [bpmEditableDesign, setBpmEditableDesign] = useState(null);
  const [bpmSimulateResult, setBpmSimulateResult] = useState(null);
  const [bpmSimulateLoading, setBpmSimulateLoading] = useState(false);
  const [bpmPathSimulateResult, setBpmPathSimulateResult] = useState(null);
  const [bpmDevWizardStep, setBpmDevWizardStep] = useState(0);
  const [bpmDesignerPane, setBpmDesignerPane] = useState('tasks');
  const [selectedBpmTask, setSelectedBpmTask] = useState(null);
  const [bpmTaskDesign, setBpmTaskDesign] = useState(null);
  const [bpmTaskDraftMongoId, setBpmTaskDraftMongoId] = useState(null);
  const [bpmTaskDraftRuleId, setBpmTaskDraftRuleId] = useState('');
  const [bpmTaskDraftSaveState, setBpmTaskDraftSaveState] = useState('idle');
  const [bpmTaskRoutingSimulate, setBpmTaskRoutingSimulate] = useState(null);
  const [bpmTaskRoutingSimulateLoading, setBpmTaskRoutingSimulateLoading] = useState(false);
  const [bpmTaskCompareResult, setBpmTaskCompareResult] = useState(null);
  const [bpmTaskCompareLoading, setBpmTaskCompareLoading] = useState(false);
  const [bpmTaskTenantSlice, setBpmTaskTenantSlice] = useState(null);
  const [bpmTaskConfigResolving] = useState(false);
  const [bpmTaskConfiguration, setBpmTaskConfiguration] = useState(null);
  const [bpmTaskConfigurationLoading, setBpmTaskConfigurationLoading] = useState(false);
  const [bpmTaskConfigurationError, setBpmTaskConfigurationError] = useState('');
  const [bpmTaskValidateResult, setBpmTaskValidateResult] = useState(null);
  const [bpmTaskValidateLoading, setBpmTaskValidateLoading] = useState(false);
  const [bpmExistingApprovals, setBpmExistingApprovals] = useState(null);
  const [bpmExistingApprovalsLoading, setBpmExistingApprovalsLoading] = useState(false);
  const [bpmTaskListMode, setBpmTaskListMode] = useState('configured');
  const [bpmRuntimeSamples, setBpmRuntimeSamples] = useState([]);
  const [bpmRuntimeLoading, setBpmRuntimeLoading] = useState(false);
  const [bpmFlowStep, setBpmFlowStep] = useState(0);
  const [bpmTxnPicked, setBpmTxnPicked] = useState(false);

  const effectiveSystem = systemPrompt || config.system;

  const bpmDesignForPush = bpmEditableDesign || bpmDesignResult?.design;

  // Live elapsed-seconds counter while a rule is being generated (OCI calls can
  // take 20–30s) so the UI clearly shows work is in progress.
  useEffect(() => {
    if (!bpmNlRuleLoading) return undefined;
    setBpmGenSeconds(0);
    const id = setInterval(() => setBpmGenSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [bpmNlRuleLoading]);

  const updateBpmPrimaryCondition = useCallback((patch) => {
    setBpmEditableDesign((d) => {
      if (!d) return d;
      const prev = Array.isArray(d.conditions) && d.conditions[0] ? d.conditions[0] : {};
      const next = { ...prev, ...patch };
      const description = buildConditionDescription(next);
      const expressionHint =
        next.table && next.column
          ? `${next.table}.${next.column} ${next.operator || 'equals'} '${next.value ?? ''}'`
          : prev.expressionHint || '';
      const rest = Array.isArray(d.conditions) ? d.conditions.slice(1) : [];
      return {
        ...d,
        conditions: [{ ...next, description, expressionHint }, ...rest],
      };
    });
  }, []);

  const isBpmFeature = feature === 'bpm';
  const isBpmTenantView = isBpmFeature && bpmLayout === 'designer';
  const isBpmWorkflowView = isBpmFeature && bpmLayout === 'landing';

  const loadBpmApprovalConfig = useCallback(async () => {
    if (!isBpmTenantView) return;
    setBpmApprovalConfigLoading(true);
    setBpmApprovalConfigErr('');
    try {
      const res = await API.get('/api/copilot/bpm/approval-config', { timeout: 120000 });
      const data = res.data;
      setBpmApprovalConfig(data);
      if (data?.inferredRouting?.length) setBpmConfigSection('inferred');
      else if (data?.liveEndpoints?.length) setBpmConfigSection('pod');
      else if (data?.rules?.length) setBpmConfigSection('rules');
      else if (data?.approverDirectory?.length) setBpmConfigSection('directory');
    } catch (err) {
      setBpmApprovalConfig(null);
      setBpmApprovalConfigErr(
        err.response?.data?.error || err.message || 'Failed to load tenant approval configuration'
      );
    } finally {
      setBpmApprovalConfigLoading(false);
    }
  }, [isBpmTenantView]);

  const loadBpmTasks = useCallback(
    async (overrides = {}) => {
      if (!isBpmTenantView) return;
      setBpmTasksLoading(true);
      setBpmTasksErr('');
      try {
        const q = overrides.q ?? bpmTaskSearch;
        const category = overrides.category ?? bpmTaskCategory;
        const res = await API.get('/api/copilot/bpm/tasks', {
          params: {
            ...(q ? { q } : {}),
            ...(category && category !== 'all' ? { category } : {}),
          },
        });
        setBpmTasksData(res.data);
      } catch (err) {
        setBpmTasksData(null);
        setBpmTasksErr(
          err.response?.data?.error || err.message || 'Failed to load HCM approval tasks'
        );
      } finally {
        setBpmTasksLoading(false);
      }
    },
    [isBpmTenantView, bpmTaskSearch, bpmTaskCategory]
  );

  const loadBpmPushTargets = useCallback(async () => {
    if (!isBpmWorkflowView) return;
    try {
      const [cfgRes, targetsRes] = await Promise.all([
        API.get('/api/copilot/bpm/push-config'),
        API.get('/api/copilot/bpm/push-targets'),
      ]);
      setBpmPushConfig(cfgRes.data);
      const targets = targetsRes.data?.targets || [];
      setBpmPushTargets(targets);
      const active = targets.find((t) => t.isActive);
      setBpmPushTargetId((prev) => {
        if (prev && targets.some((t) => t.id === prev)) return prev;
        return active?.id || targets[0]?.id || '';
      });
    } catch (_) {
      setBpmPushConfig({ enabled: false });
      setBpmPushTargets([]);
    }
  }, [isBpmWorkflowView]);

  const loadBpmHcmConnection = useCallback(async () => {
    if (!isBpmTenantView && !isBpmWorkflowView) return;
    try {
      const res = await API.get('/api/hcm/connection', { validateStatus: () => true });
      if (res.status === 200 && res.data) {
        setBpmHcmConn(res.data);
        return;
      }
      const list = await API.get('/api/connections', { validateStatus: () => true });
      const saved = (list.data?.connections || []).find((c) => c.baseUrl && c.username);
      setBpmHcmConn(
        saved
          ? { isConfigured: true, baseUrl: saved.baseUrl, name: saved.name }
          : null
      );
    } catch {
      setBpmHcmConn(null);
    }
  }, [isBpmTenantView, isBpmWorkflowView]);

  const loadBpmExistingApprovals = useCallback(async () => {
    if (!isBpmTenantView) return;
    setBpmExistingApprovalsLoading(true);
    try {
      const res = await API.get('/api/copilot/bpm/existing-approvals', { timeout: 120000 });
      setBpmExistingApprovals(res.data);
    } catch {
      setBpmExistingApprovals(null);
    } finally {
      setBpmExistingApprovalsLoading(false);
    }
  }, [isBpmTenantView]);

  const refreshBpmTenantPanel = useCallback(async () => {
    await Promise.all([
      loadBpmHcmConnection(),
      loadBpmTasks(),
      loadBpmApprovalConfig(),
      loadBpmExistingApprovals(),
    ]);
  }, [loadBpmHcmConnection, loadBpmTasks, loadBpmApprovalConfig, loadBpmExistingApprovals]);

  const refreshBpmWorkflowPanel = useCallback(async () => {
    await Promise.all([loadBpmHcmConnection(), loadBpmPushTargets()]);
  }, [loadBpmHcmConnection, loadBpmPushTargets]);

  const bpmTasksFilterReady = useRef(false);

  useEffect(() => {
    API.get('/api/ai/health')
      .then((res) => setProviderStatus(res.data))
      .catch(() => setProviderStatus(null));
  }, []);

  useEffect(() => {
    if (!isBpmWorkflowView) {
      setApiHealth(null);
      setApiHealthBanner('');
      return;
    }
    API.get('/api/health', { validateStatus: () => true })
      .then((res) => {
        if (res.status !== 200) {
          setApiHealth(null);
          setApiHealthBanner(
            `API health returned HTTP ${res.status}. Restart node server.js (port 3010) from latest main.`
          );
          return;
        }
        setApiHealth(res.data);
        const f = res.data?.features || {};
        const missing = [];
        if (f.podCatalogColumns !== true) missing.push('podCatalogColumns');
        if (f.bpmAttributes !== true) missing.push('bpmAttributes');
        if (f.bpmGenerateRuleFromNl !== true) missing.push('bpmGenerateRuleFromNl');
        if (missing.length) {
          setApiHealthBanner(
            `Stale API build (missing ${missing.join(', ')}). Stop old node processes and restart: ` +
              'scripts/restart-dev.ps1 or node server.js from C:\\dev\\AI.'
          );
        } else {
          setApiHealthBanner('');
        }
      })
      .catch(() => {
        setApiHealth(null);
        setApiHealthBanner(
          'Cannot reach GET /api/health — confirm the API is running and REACT_APP_PROXY_TARGET matches its port.'
        );
      });
  }, [isBpmWorkflowView]);

  useEffect(() => {
    if (!isBpmTenantView) {
      bpmTasksFilterReady.current = false;
      return undefined;
    }
    if (!bpmTasksFilterReady.current) {
      bpmTasksFilterReady.current = true;
      refreshBpmTenantPanel();
      return undefined;
    }
    const delay = bpmTaskSearch ? 350 : 0;
    const t = setTimeout(() => loadBpmTasks(), delay);
    return () => clearTimeout(t);
  }, [isBpmTenantView, bpmTaskSearch, bpmTaskCategory, refreshBpmTenantPanel, loadBpmTasks]);

  useEffect(() => {
    if (!isBpmWorkflowView) return undefined;
    refreshBpmWorkflowPanel();
    API.get('/api/copilot/bpm/design-packs', { validateStatus: () => true })
      .then((res) => {
        if (res.status === 200) setBpmDesignPacks(res.data?.packs || []);
      })
      .catch(() => setBpmDesignPacks([]));
    return undefined;
  }, [isBpmWorkflowView, refreshBpmWorkflowPanel]);

  useEffect(() => {
    if (bpmDesignResult?.ok && bpmDesignResult.design) {
      setBpmEditableDesign(JSON.parse(JSON.stringify(bpmDesignResult.design)));
      setBpmDevWizardStep(0);
      setBpmSimulateResult(null);
      setBpmPathSimulateResult(null);
    }
  }, [bpmDesignResult]);

  const handleBpmDesignChange = useCallback((next) => {
    setBpmEditableDesign(next);
    setBpmPathSimulateResult(null);
  }, []);

  // Auto-send a prefilled prompt when another page hands off (e.g. UserLookup).
  // Use a ref-style guard via the handoff identity itself: we only fire once per
  // distinct handoff payload, then notify the parent to clear it.
  useEffect(() => {
    if (!handoff || !handoff.prompt) return;
    if (isBpmFeature) {
      setBpmStructuredPolicy(handoff.prompt);
      setBpmTxnPicked(true);
      setBpmFlowStep(1);
      setBpmDesignExpandSignal((n) => n + 1);
      onHandoffConsumed?.();
      return;
    }
    send(handoff.prompt, handoff.context || null);
    onHandoffConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const turnIndex = useMemo(() => messages.filter((m) => m.role === 'user').length, [messages]);

  function resetConversation() {
    setMessages([]);
    setError('');
    setConversationId(newConversationId());
  }

  async function send(rawText, extraContext) {
    const text = String(rawText ?? input).trim();
    if (!text || sending) return;
    setError('');

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    const mergedContext =
      extraContext && Object.keys(extraContext).length
        ? { ...(context || {}), ...extraContext }
        : context;

    try {
      const wireMessages = nextMessages.map((m) => ({ role: m.role, content: m.content }));
      const res = await API.post('/api/ai/chat', {
        feature,
        system: effectiveSystem,
        messages: wireMessages,
        conversationId,
        turnIndex,
        context: mergedContext,
      });
      const data = res.data || {};
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.text || '(empty response)',
          meta: {
            model: data.model,
            provider: data.provider,
            tokens: data.tokens,
            costUsd: data.costUsd,
            latencyMs: data.latencyMs,
            interactionId: data.interactionId,
            finishReason: data.finishReason,
          },
        },
      ]);
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || 'AI request failed';
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  async function recordVerdict(interactionId, verdict) {
    try {
      await API.post(`/api/ai/interactions/${interactionId}/verdict`, { verdict });
      setMessages((prev) =>
        prev.map((m) =>
          m.meta && m.meta.interactionId === interactionId
            ? { ...m, meta: { ...m.meta, verdict } }
            : m
        )
      );
    } catch (_) {
      // ignore — verdict is best-effort
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    send();
  }

  async function runStructuredBpmDesign() {
    const policy = String(bpmStructuredPolicy || '').trim();
    if (!policy || bpmDesignLoading || bpmNlRuleLoading) return;
    setError('');
    setBpmNlOk(false);
    setBpmDesignResult(null);
    setBpmDesignLoading(true);
    try {
      const res = await API.post('/api/copilot/bpm/design', {
        policy,
        ...(bpmActivePackId ? { designPackId: bpmActivePackId } : {}),
        transactionCategory: bpmPushCategory,
      });
      setBpmDesignResult(res.data);
      if (res.data?.ok) {
        setBpmDesignExpandSignal((n) => n + 1);
      } else {
        setError(formatBpmNlApiFailure(res.data) || 'Structured design did not return a valid plan.');
      }
    } catch (err) {
      setError(formatCopilotHttpError(err, 'Structured design request failed'));
    } finally {
      setBpmDesignLoading(false);
    }
  }

  async function runGenerateRuleFromNl() {
    const prompt = String(bpmStructuredPolicy || '').trim();
    if (!prompt || bpmNlRuleLoading || bpmDesignLoading) return;
    setError('');
    setBpmNotice('');
    setBpmNlOk(false);
    setBpmDesignResult(null);
    setBpmNlRuleLoading(true);
    try {
      const res = await API.post(
        '/api/copilot/bpm/generate-rule-from-nl',
        {
          prompt,
          ...(bpmEditableDesign ? { existingDesign: bpmEditableDesign } : {}),
          transactionCategory: bpmPushCategory,
        },
        { timeout: 120000 }
      );
      setBpmDesignResult(res.data);
      if (res.data?.ok) {
        setBpmEditableDesign(JSON.parse(JSON.stringify(res.data.design)));
        setBpmDevWizardStep(0);
        setBpmSimulateResult(null);
        setBpmPathSimulateResult(null);
        setBpmDesignExpandSignal((n) => n + 1);
        setBpmNlOk(true);
        setBpmFlowStep(2);
        const notices = [
          res.data?.mockNotice,
          res.data?.devContextWarning,
          ...(Array.isArray(res.data?.warnings) ? res.data.warnings.slice(0, 2) : []),
        ].filter(Boolean);
        if (notices.length) setBpmNotice(notices.join(' '));
      } else {
        setError(formatBpmNlApiFailure(res.data));
      }
    } catch (err) {
      setError(formatCopilotHttpError(err, 'Generate rule from natural language failed'));
    } finally {
      setBpmNlRuleLoading(false);
    }
  }

  function downloadBpmDesignJson() {
    if (!bpmDesignForPush) return;
    const blob = new Blob([JSON.stringify(bpmDesignForPush, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bpm-approval-design-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadBpmManualExport() {
    const payload = bpmPushResult?.manualExport;
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bpm-fusion-manual-export-${bpmPushCategory}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function applyBpmDesignPack(pack) {
    if (!pack) return;
    setBpmActivePackId(pack.id);
    setBpmStructuredPolicy(pack.policyTemplate || '');
    setBpmPushCategory(pack.id === 'time_labor' ? 'time_labor' : 'absence');
    setBpmTxnPicked(true);
    setBpmFlowStep(1);
    setBpmDesignExpandSignal((n) => n + 1);
  }

  function pickBpmTransaction(id) {
    setBpmPushCategory(id === 'time_labor' ? 'time_labor' : 'absence');
    setBpmTxnPicked(true);
    const pack = bpmDesignPacks.find((p) => p.id === id);
    if (pack) {
      setBpmActivePackId(pack.id);
      if (!bpmStructuredPolicy.trim() && pack.policyTemplate) {
        setBpmStructuredPolicy(pack.policyTemplate);
      }
    }
    setBpmFlowStep(1);
  }

  async function runBpmSimulate() {
    if (!bpmDesignForPush || bpmSimulateLoading) return;
    setBpmSimulateLoading(true);
    setBpmSimulateResult(null);
    setError('');
    try {
      const res = await API.post('/api/copilot/bpm/simulate', {
        design: bpmDesignForPush,
        category: bpmPushCategory,
      });
      setBpmSimulateResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Simulation failed');
    } finally {
      setBpmSimulateLoading(false);
    }
  }

  async function runBpmPush(dryRun) {
    if (!bpmDesignResult?.ok || !bpmDesignForPush || bpmPushLoading) return;
    if (!dryRun) {
      const confirmed = window.confirm(
        'This will push the current design directly to Oracle HCM via the sync engine.\n\n' +
          'Prefer saving as a draft and using Deploy in the Approval Designer toolbar.\n\nContinue?'
      );
      if (!confirmed) return;
    }
    setBpmPushLoading(true);
    setBpmPushResult(null);
    setError('');
    try {
      const res = await API.post(
        '/api/copilot/bpm/push',
        {
          design: bpmDesignForPush,
          category: bpmPushCategory,
          connectionId: bpmPushTargetId || undefined,
          dryRun,
        },
        { timeout: 120000 }
      );
      setBpmPushResult(res.data);
      if (res.data?.ok !== false) setBpmFlowStep(3);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'BPM push failed';
      setError(msg);
      if (err.response?.data) {
        setBpmPushResult({ ok: false, error: msg, code: err.response.data.code });
      }
    } finally {
      setBpmPushLoading(false);
    }
  }

  const bpmPushEnabled = bpmPushConfig?.enabled === true;
  const bpmCanPush =
    bpmPushEnabled && bpmDesignResult?.ok && bpmDesignForPush && bpmPushTargetId;

  const bpmProbeFailed = useMemo(
    () =>
      (bpmApprovalConfig?.endpointProbe || []).filter(
        (e) => e.status !== 200 || (e.itemCount ?? 0) === 0
      ),
    [bpmApprovalConfig?.endpointProbe]
  );
  const bpmTenantConfigTabs = useMemo(
    () => getBpmTenantConfigTabs(bpmApprovalConfig, bpmProbeFailed),
    [bpmApprovalConfig, bpmProbeFailed]
  );
  const bpmActiveConfigTab = bpmTenantConfigTabs.find((t) => t.id === bpmConfigSection);
  const bpmDevTestSteps =
    bpmDesignForPush?.devTestPlan?.length > 0
      ? bpmDesignForPush.devTestPlan
      : bpmDesignResult?.checklist?.length > 0
        ? bpmDesignResult.checklist
        : [];

  const selectedBpmTaskTenantSlice = useMemo(() => {
    if (!selectedBpmTask) return null;
    if (bpmTaskTenantSlice) return bpmTaskTenantSlice;
    if (!bpmApprovalConfig) return null;
    return filterTenantConfigForTask(bpmApprovalConfig, selectedBpmTask);
  }, [selectedBpmTask, bpmApprovalConfig, bpmTaskTenantSlice]);

  const bpmTaskTransactionCategory = useMemo(() => {
    if (!selectedBpmTask) return 'absence';
    return taskCategoryToTransactionCategory(selectedBpmTask.category);
  }, [selectedBpmTask]);

  const loadBpmTaskDraftForSelection = useCallback(
    async (task) => {
      if (!task?.name) return;
      const slice = filterTenantConfigForTask(bpmApprovalConfig, task);
      const ruleId = slugTaskRuleId(task.name);
      setBpmTaskDraftRuleId(ruleId);
      setBpmTaskDraftMongoId(null);
      setBpmTaskRoutingSimulate(null);
      setBpmTaskCompareResult(null);
      const nameKey = normalizeTaskNameKey(task.name);
      try {
        const drafts = await listApprovalDrafts(API);
        const existing = drafts.find(
          (d) =>
            normalizeTaskNameKey(d.transaction) === nameKey ||
            normalizeTaskNameKey(d.name) === nameKey ||
            normalizeTaskNameKey(d.design?.transactionHint) === nameKey ||
            d.id === ruleId
        );
        if (existing?.design?.stages?.length) {
          setBpmTaskDesign(JSON.parse(JSON.stringify(existing.design)));
          setBpmTaskDraftMongoId(existing.mongoId || null);
          setBpmTaskDraftRuleId(existing.id || ruleId);
          return;
        }
      } catch (_) {
        /* drafts optional — fall through to tenant seed */
      }

      if (slice.rules?.length) {
        setBpmTaskDesign(designFromTenantRules(slice.rules, task.name));
      } else if (slice.inferredRouting?.length) {
        setBpmTaskDesign(designFromInferredRouting(slice.inferredRouting, task.name));
      } else {
        setBpmTaskDesign(designFromTenantRules([], task.name));
      }
    },
    [bpmApprovalConfig]
  );

  useEffect(() => {
    if (!selectedBpmTask || !bpmApprovalConfig) return;
    setBpmTaskTenantSlice(filterTenantConfigForTask(bpmApprovalConfig, selectedBpmTask));
  }, [selectedBpmTask, bpmApprovalConfig]);

  const loadBpmTaskConfiguration = useCallback(async (task) => {
    if (!task?.name) {
      setBpmTaskConfiguration(null);
      return;
    }
    const taskKey = slugTaskRuleId(task.name).replace(/^task-/, '');
    setBpmTaskConfigurationLoading(true);
    setBpmTaskConfigurationError('');
    setBpmTaskValidateResult(null);
    try {
      const res = await API.get(`/api/copilot/bpm/tasks/${encodeURIComponent(taskKey)}/configuration`, {
        params: {
          name: task.name,
          ...(task.code ? { code: task.code } : {}),
          ...(task.category ? { category: task.category } : {}),
          confirmedOnTenant: task.confirmedOnTenant ? 'true' : 'false',
        },
        timeout: 120000,
      });
      setBpmTaskConfiguration(res.data);
      if (res.data?.tenantSlice) {
        setBpmTaskTenantSlice(res.data.tenantSlice);
      }
    } catch (err) {
      let usedFallback = false;
      try {
        const params = {
          name: task.name,
          ...(task.code ? { code: task.code } : {}),
          ...(task.category ? { category: task.category } : {}),
        };
        const [tcRes, eaRes] = await Promise.all([
          API.get('/api/copilot/bpm/task-config', { params, timeout: 120000 }).catch(() => null),
          API.get('/api/copilot/bpm/existing-approvals', { timeout: 120000 }).catch(() => null),
        ]);
        const fallback = buildFallbackTaskConfiguration(
          task,
          tcRes?.data,
          eaRes?.data || bpmExistingApprovals,
          bpmApprovalConfig?.baseUrl
        );
        setBpmTaskConfiguration(fallback);
        if (fallback.tenantSlice) setBpmTaskTenantSlice(fallback.tenantSlice);
        usedFallback = true;
        setBpmTaskConfigurationError('');
      } catch (_) {
        /* keep primary error below */
      }
      if (!usedFallback) {
        setBpmTaskConfigurationError(
          formatCopilotHttpError(err, 'Failed to load task configuration', {
            fusionBaseUrl: bpmApprovalConfig?.baseUrl,
          })
        );
        setBpmTaskConfiguration(null);
      }
    } finally {
      setBpmTaskConfigurationLoading(false);
    }
  }, [bpmApprovalConfig?.baseUrl, bpmExistingApprovals]);

  const resolveBpmTaskTenantSlice = useCallback(
    async (task) => {
      if (!task?.name && !task?.code) {
        setBpmTaskTenantSlice(null);
        return;
      }
      const localSlice = bpmApprovalConfig
        ? filterTenantConfigForTask(bpmApprovalConfig, task)
        : null;
      if (localSlice) setBpmTaskTenantSlice(localSlice);
      await loadBpmTaskConfiguration(task);
    },
    [bpmApprovalConfig, loadBpmTaskConfiguration]
  );

  const loadBpmRuntimeSample = useCallback(async () => {
    if (!selectedBpmTask?.name || bpmRuntimeLoading) return;
    setBpmRuntimeLoading(true);
    try {
      const res = await API.get('/api/copilot/bpm/approval-config', { timeout: 120000 });
      const slice = filterTenantConfigForTask(res.data, selectedBpmTask);
      setBpmRuntimeSamples(slice.bpmTasks || slice.notifications || []);
    } catch {
      setBpmRuntimeSamples([]);
    } finally {
      setBpmRuntimeLoading(false);
    }
  }, [selectedBpmTask, bpmRuntimeLoading]);

  const configuredTaskNames = useMemo(() => {
    const names = new Set();
    for (const row of bpmExistingApprovals?.timeLabor || []) {
      if (row?.name) names.add(normalizeTaskNameKey(row.name));
    }
    for (const row of bpmExistingApprovals?.absences || []) {
      if (row?.name) names.add(normalizeTaskNameKey(row.name));
    }
    return names;
  }, [bpmExistingApprovals]);

  const handleBpmTaskDesignChange = useCallback((next) => {
    setBpmTaskDesign(next);
    setBpmTaskRoutingSimulate(null);
  }, []);

  function downloadBpmApprovalConfigJson() {
    if (!bpmApprovalConfig) return;
    const blob = new Blob([JSON.stringify(bpmApprovalConfig, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bpm-tenant-approval-config-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function selectBpmTask(task) {
    if (!task?.name) return;
    if (isBpmTenantView) {
      const same =
        selectedBpmTask && normalizeTaskNameKey(selectedBpmTask.name) === normalizeTaskNameKey(task.name);
      if (same) {
        setSelectedBpmTask(null);
        setBpmTaskDesign(null);
        setBpmTaskDraftMongoId(null);
        setBpmTaskRoutingSimulate(null);
        setBpmTaskCompareResult(null);
        setBpmTaskTenantSlice(null);
        setBpmTaskConfiguration(null);
        setBpmTaskConfigurationError('');
        setBpmTaskValidateResult(null);
        return;
      }
      setSelectedBpmTask(task);
      setBpmTaskTenantSlice(null);
      setBpmTaskConfiguration(null);
      loadBpmTaskDraftForSelection(task);
      resolveBpmTaskTenantSlice(task);
      return;
    }
    const name = task.name || 'this transaction';
    const prompt =
      `Design an approval rule for the Fusion HCM transaction "${name}". ` +
      'Include typical approver routing for this transaction type.';
    setBpmStructuredPolicy(prompt);
    setBpmDesignExpandSignal((n) => n + 1);
  }

  async function saveBpmTaskDraft() {
    if (!selectedBpmTask || !bpmTaskDesign?.stages?.length) return;
    setBpmTaskDraftSaveState('saving');
    setError('');
    try {
      const payload = {
        ruleId: bpmTaskDraftRuleId || slugTaskRuleId(selectedBpmTask.name),
        ruleName: selectedBpmTask.name,
        transaction: selectedBpmTask.name,
        transactionCategory: bpmTaskTransactionCategory,
        design: bpmTaskDesign,
        summary: bpmTaskDesign.summary || '',
      };
      const saved = bpmTaskDraftMongoId
        ? await updateApprovalDraft(API, bpmTaskDraftMongoId, payload)
        : await saveApprovalDraft(API, payload);
      setBpmTaskDraftMongoId(saved.mongoId);
      setBpmTaskDraftRuleId(saved.id);
      setBpmTaskDraftSaveState('saved');
    } catch (err) {
      setBpmTaskDraftSaveState('error');
      setError(formatDraftApiError(err, 'Failed to save task draft'));
    }
  }

  async function runBpmTaskRoutingSimulate() {
    if (!bpmTaskDesign?.stages?.length || bpmTaskRoutingSimulateLoading) return;
    setBpmTaskRoutingSimulateLoading(true);
    setBpmTaskRoutingSimulate(null);
    setError('');
    try {
      const res = await API.post('/api/copilot/bpm/simulate', {
        design: bpmTaskDesign,
        category: bpmTaskTransactionCategory,
        tenantConfig: bpmApprovalConfig || undefined,
      });
      setBpmTaskRoutingSimulate(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Simulation failed');
    } finally {
      setBpmTaskRoutingSimulateLoading(false);
    }
  }

  async function runBpmTaskValidate() {
    if (!selectedBpmTask?.name || !bpmTaskDesign?.stages?.length || bpmTaskValidateLoading) return;
    const taskKey = slugTaskRuleId(selectedBpmTask.name).replace(/^task-/, '');
    setBpmTaskValidateLoading(true);
    setBpmTaskValidateResult(null);
    setError('');
    try {
      const res = await API.post(
        `/api/copilot/bpm/tasks/${encodeURIComponent(taskKey)}/validate`,
        {
          design: bpmTaskDesign,
          taskName: selectedBpmTask.name,
          taskCode: selectedBpmTask.code,
          category: selectedBpmTask.category,
          transactionCategory: bpmTaskTransactionCategory,
        },
        { timeout: 60000 }
      );
      setBpmTaskValidateResult(res.data);
    } catch (err) {
      setBpmTaskValidateResult({
        ok: false,
        issues: [{ severity: 'error', message: err.response?.data?.error || err.message || 'Validate failed' }],
      });
    } finally {
      setBpmTaskValidateLoading(false);
    }
  }

  async function runBpmTaskCompare() {
    if (!bpmTaskDraftMongoId || bpmTaskCompareLoading) return;
    setBpmTaskCompareLoading(true);
    setBpmTaskCompareResult(null);
    setError('');
    try {
      const data = await compareApprovalDraft(API, bpmTaskDraftMongoId);
      setBpmTaskCompareResult(data);
    } catch (err) {
      setError(formatDraftApiError(err, 'Compare failed'));
    } finally {
      setBpmTaskCompareLoading(false);
    }
  }

  const bpmTasksByCategory = useMemo(() => {
    const tasks = dedupeBpmTasks(bpmTasksData?.tasks || []);
    const groups = new Map();
    for (const t of tasks) {
      const cat = bpmTasksData?.categories?.find((c) => c.id === t.category);
      const label = cat?.label || t.category || 'Other';
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(t);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [bpmTasksData]);

  const isMock = providerStatus?.provider?.selected === 'mock';

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
      <div className={`flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white rounded-t-xl ${isBpmWorkflowView ? 'px-3 py-2' : 'px-5 py-3'}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-800 truncate">
              {title || config.label}
            </h2>
            {subtitle ? (
              <p className="text-xs text-slate-500 truncate">{subtitle}</p>
            ) : isMock ? (
              <p className="text-xs text-amber-600 truncate">
                Mock provider — set OCI_* env vars to enable OCI Generative AI.
              </p>
            ) : null}
          </div>
        </div>
        {isBpmTenantView ? (
          <button
            type="button"
            onClick={refreshBpmTenantPanel}
            disabled={bpmTasksLoading || bpmApprovalConfigLoading}
            className="flex items-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-900 hover:bg-cyan-100 disabled:opacity-50"
            title="Refresh tenant tasks and approval configuration"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${bpmTasksLoading || bpmApprovalConfigLoading ? 'animate-spin' : ''}`}
            />
            Refresh tenant data
          </button>
        ) : !isBpmWorkflowView ? (
          <button
            type="button"
            onClick={resetConversation}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            title="New chat"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            New chat
          </button>
        ) : null}
      </div>

      {isBpmTenantView && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
          {bpmDesignerPane === 'tenant-info' ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
              <aside
                className="flex w-full shrink-0 flex-col border-b border-violet-100 bg-violet-50/30 lg:w-72 lg:max-w-[320px] lg:border-b-0 lg:border-r"
                aria-label="Tenant configuration sections"
              >
                {!bpmHcmConn?.isConfigured ? (
                  <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-3 py-2.5 text-xs text-amber-950">
                    <p className="font-semibold text-amber-900">No Fusion tenant connected</p>
                    <p className="mt-1">Open Connections to connect your pod.</p>
                  </div>
                ) : (
                  <div className="shrink-0 border-b border-emerald-100 bg-emerald-50/80 px-3 py-2 text-[11px] text-emerald-950">
                    <span className="font-semibold text-emerald-900">Tenant connected</span>
                    <span className="block truncate text-emerald-800/90">
                      {bpmHcmConn.baseUrl?.replace(/^https?:\/\//, '') || 'Fusion'}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 border-b border-violet-100 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-900">
                    Configuration
                  </p>
                  <div className="flex items-center gap-1">
                    {bpmApprovalConfigLoading && (
                      <Loader2 className="h-4 w-4 animate-spin text-violet-600" aria-label="Loading" />
                    )}
                    {bpmApprovalConfig?.hasConfig && (
                      <button
                        type="button"
                        onClick={downloadBpmApprovalConfigJson}
                        className="inline-flex items-center gap-1 rounded border border-violet-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-violet-900 hover:bg-violet-50"
                        title="Download full configuration JSON"
                      >
                        <Download className="h-3 w-3" />
                        JSON
                      </button>
                    )}
                  </div>
                </div>

                <ul className="min-h-0 flex-1 overflow-y-auto divide-y divide-violet-100/80 p-2">
                  {bpmTenantConfigTabs.length === 0 ? (
                    <li className="px-2 py-3 text-xs text-slate-500">
                      {bpmApprovalConfigLoading
                        ? 'Loading sections…'
                        : 'Refresh tenant data to load configuration sections.'}
                    </li>
                  ) : (
                    bpmTenantConfigTabs.map((tab) => {
                      const active = bpmConfigSection === tab.id;
                      return (
                        <li key={tab.id}>
                          <button
                            type="button"
                            onClick={() => setBpmConfigSection(tab.id)}
                            className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors ${
                              active
                                ? 'bg-violet-100 font-medium text-violet-950 ring-1 ring-violet-200'
                                : 'text-slate-700 hover:bg-violet-50/80'
                            }`}
                            aria-current={active ? 'true' : undefined}
                          >
                            <span className="min-w-0 flex-1">{tab.label}</span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </aside>

              <main
                className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                aria-label="Tenant configuration detail"
              >
                <div className="shrink-0 border-b border-slate-100 bg-white px-4 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {bpmActiveConfigTab?.label || 'Configuration detail'}
                  </p>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  <BpmTenantApprovalConfigPanel
                    config={bpmApprovalConfig}
                    error={bpmApprovalConfigErr}
                    loading={bpmApprovalConfigLoading}
                    section={bpmConfigSection}
                    onSectionChange={setBpmConfigSection}
                    probeFailed={bpmProbeFailed}
                    showNav={false}
                    showHeader
                    contentClassName="min-h-[200px] max-h-none border-0 bg-transparent"
                  />
                </div>
              </main>
            </div>
          ) : (
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row"
              aria-label="Fusion tenant approval tasks"
            >
              <aside
                className="flex w-full shrink-0 flex-col border-b border-cyan-100 bg-cyan-50/20 lg:w-80 lg:max-w-[360px] lg:border-b-0 lg:border-r"
              >
                {!bpmHcmConn?.isConfigured ? (
                  <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-3 py-2.5 text-xs text-amber-950">
                    <p className="font-semibold text-amber-900">No Fusion tenant connected</p>
                    <p className="mt-1">Open Connections to connect your pod.</p>
                  </div>
                ) : (
                  <div className="shrink-0 border-b border-emerald-100 bg-emerald-50/80 px-3 py-2 text-[11px] text-emerald-950">
                    <span className="font-semibold text-emerald-900">Tenant connected</span>
                    <span className="block truncate text-emerald-800/90">
                      {bpmHcmConn.baseUrl?.replace(/^https?:\/\//, '') || 'Fusion'}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 border-b border-cyan-100 px-3 py-2">
                  <div className="min-w-0">
                    <h3
                      id="bpm-tasks-heading"
                      className="text-xs font-semibold uppercase tracking-wide text-cyan-900"
                    >
                      Approval tasks
                    </h3>
                  </div>
                  {bpmTasksLoading ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-600" aria-label="Loading tasks" />
                  ) : null}
                </div>

                <div className="shrink-0 space-y-2 border-b border-cyan-100 p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="search"
                      value={bpmTaskSearch}
                      onChange={(e) => setBpmTaskSearch(e.target.value)}
                      placeholder="Search tasks…"
                      className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-2.5 text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setBpmTaskListMode('configured')}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                        bpmTaskListMode === 'configured'
                          ? 'bg-emerald-700 text-white'
                          : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Configured
                      {bpmExistingApprovals?.timeLabor
                        ? ` (${bpmExistingApprovals.timeLabor.length})`
                        : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBpmTaskListMode('catalog')}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                        bpmTaskListMode === 'catalog'
                          ? 'bg-cyan-700 text-white'
                          : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Seeded
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setBpmTaskCategory('all')}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                        bpmTaskCategory === 'all'
                          ? 'bg-cyan-700 text-white'
                          : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      All{bpmTasksData ? ` (${bpmTasksData.totalCount})` : ''}
                    </button>
                    {(bpmTasksData?.categories || []).map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setBpmTaskCategory(cat.id)}
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                          bpmTaskCategory === cat.id
                            ? 'bg-cyan-700 text-white'
                            : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {cat.label} ({cat.count})
                      </button>
                    ))}
                  </div>
                  {bpmTasksErr && (
                    <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-2 text-xs text-rose-800">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span className="break-words">{bpmTasksErr}</span>
                    </div>
                  )}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                  {bpmTaskListMode === 'configured' ? (
                    <BpmConfiguredApprovalsList
                      loading={bpmExistingApprovalsLoading}
                      timeLabor={bpmExistingApprovals?.timeLabor}
                      absences={bpmExistingApprovals?.absences}
                      selectedTaskName={selectedBpmTask?.name}
                      onSelectRow={selectBpmTask}
                      showAbsences={bpmTaskCategory === 'absence' || bpmTaskCategory === 'all'}
                    />
                  ) : bpmTasksByCategory.length === 0 && !bpmTasksLoading ? (
                    <p className="p-3 text-xs text-slate-500">No tasks match your search.</p>
                  ) : (
                    bpmTasksByCategory.map(([categoryLabel, tasks]) => (
                      <div key={categoryLabel} className="border-b border-slate-100 last:border-b-0">
                        <div className="sticky top-0 bg-cyan-50/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {categoryLabel}
                        </div>
                        <ul className="divide-y divide-slate-100">
                          {tasks.map((task) => {
                            const selected =
                              selectedBpmTask &&
                              normalizeTaskNameKey(selectedBpmTask.name) === normalizeTaskNameKey(task.name);
                            return (
                              <li key={normalizeTaskNameKey(task.name)}>
                                <button
                                  type="button"
                                  onClick={() => selectBpmTask(task)}
                                  className={`flex w-full items-start gap-2 px-2.5 py-2 text-left text-xs transition-colors ${
                                    selected
                                      ? 'bg-cyan-100 font-medium text-cyan-950 ring-1 ring-inset ring-cyan-200'
                                      : 'hover:bg-cyan-50 text-slate-800'
                                  }`}
                                  aria-current={selected ? 'true' : undefined}
                                >
                                  <span className="min-w-0 flex-1">{task.name}</span>
                                  {(() => {
                                    const onSql = configuredTaskNames.has(normalizeTaskNameKey(task.name));
                                    const badge = resolveSourceBadgeLabel(
                                      onSql ? 'sql' : task.confirmedOnTenant ? 'tenant' : 'catalog'
                                    );
                                    return (
                                      <span
                                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}
                                      >
                                        {badge.text}
                                      </span>
                                    );
                                  })()}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </aside>

              <main
                className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white"
                aria-label="Task configuration detail"
              >
                <div className="shrink-0 border-b border-slate-100 bg-white px-4 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Task configuration
                  </p>
                </div>
                <BpmTaskDetailPanel
                  task={selectedBpmTask}
                  tenantSlice={selectedBpmTaskTenantSlice}
                  taskConfiguration={bpmTaskConfiguration}
                  configLoading={
                    bpmApprovalConfigLoading || bpmTaskConfigResolving || bpmTaskConfigurationLoading
                  }
                  endpointProbe={
                    bpmTaskConfiguration?.dataSources?.endpointProbe ||
                    bpmApprovalConfig?.endpointProbe
                  }
                  configSummary={bpmTaskConfiguration?.summary}
                  configError={
                    bpmTaskConfigurationError ||
                    (!bpmTaskConfiguration && !bpmTaskConfigurationLoading ? bpmApprovalConfigErr : '')
                  }
                  onLoadRuntime={loadBpmRuntimeSample}
                  runtimeLoading={bpmRuntimeLoading}
                  runtimeSamples={bpmRuntimeSamples}
                  design={bpmTaskDesign}
                  onDesignChange={handleBpmTaskDesignChange}
                  transactionCategory={bpmTaskTransactionCategory}
                  api={API}
                  draftMongoId={bpmTaskDraftMongoId}
                  onSaveDraft={saveBpmTaskDraft}
                  saveState={bpmTaskDraftSaveState}
                  onRunRoutingSimulate={runBpmTaskRoutingSimulate}
                  routingSimulateLoading={bpmTaskRoutingSimulateLoading}
                  routingSimulateResult={bpmTaskRoutingSimulate}
                  onCompare={runBpmTaskCompare}
                  compareLoading={bpmTaskCompareLoading}
                  compareResult={bpmTaskCompareResult}
                  onValidate={runBpmTaskValidate}
                  validateLoading={bpmTaskValidateLoading}
                  validateResult={bpmTaskValidateResult}
                />
              </main>
            </div>
          )}
        </div>
      )}

      {isBpmWorkflowView && (
        <ApprovalCopilotLanding
          step={bpmFlowStep}
          onStepChange={setBpmFlowStep}
          txnPicked={bpmTxnPicked}
          category={bpmPushCategory}
          onPickTransaction={pickBpmTransaction}
          connected={!!bpmHcmConn?.isConfigured}
          tenantHost={bpmHcmConn?.baseUrl?.replace(/^https?:\/\//, '') || ''}
          onOpenDesigner={() => onOpenApprovalDesigner?.()}
          onOpenConnections={() => onOpenConnections?.()}
          policy={bpmStructuredPolicy}
          onPolicyChange={setBpmStructuredPolicy}
          onGenerate={runGenerateRuleFromNl}
          generating={bpmNlRuleLoading}
          genSeconds={bpmGenSeconds}
          notice={bpmNotice}
          generateOk={bpmNlOk}
          error={error}
          api={API}
          design={bpmEditableDesign}
          designForPush={bpmDesignForPush}
          designResult={bpmDesignResult}
          onDesignChange={handleBpmDesignChange}
          onSimulateResult={setBpmSimulateResult}
          onPathSimulateResult={setBpmPathSimulateResult}
          onDeployResult={(result) => {
            if (result?.deploy) {
              setBpmPushResult(result.deploy);
              setBpmFlowStep(3);
            } else if (result?.error) {
              setBpmPushResult({ ok: false, error: result.error, code: result.code });
            }
          }}
          onSimulate={runBpmSimulate}
          simulateLoading={bpmSimulateLoading}
          simulateResult={bpmSimulateResult}
          onConditionChange={updateBpmPrimaryCondition}
          designerRef={bpmDesignerRef}
          pushEnabled={bpmPushEnabled}
          canPush={bpmCanPush}
          pushTargets={bpmPushTargets}
          pushTargetId={bpmPushTargetId}
          onPushTargetChange={setBpmPushTargetId}
          onPush={runBpmPush}
          pushLoading={bpmPushLoading}
          pushResult={bpmPushResult}
        />
      )}


      {!isBpmFeature && (
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5 space-y-4 min-h-[280px]"
      >
        {messages.length === 0 && (
          <div className="mx-auto w-full max-w-md text-center text-slate-500">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
              <Sparkles className="h-7 w-7" />
            </div>
            <p className="text-sm">Start a conversation or pick a prompt below.</p>
            {feature === 'report' && (
              <QueryHintChips onPick={(q) => send(q)} className="mt-4 justify-center" />
            )}
            {config.starters.length > 0 && (
              <div className="mt-5 grid gap-2">
                {config.starters.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:border-cyan-300 hover:bg-cyan-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble
            key={i}
            role={m.role}
            content={m.content}
            meta={m.meta}
            onVerdict={recordVerdict}
          />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Thinking…
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="break-words">{error}</div>
          </div>
        )}
      </div>
      )}

      {!isBpmFeature && (
      <form
        onSubmit={handleSubmit}
        className="shrink-0 border-t border-slate-200 bg-white p-3 rounded-b-xl"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={3}
            placeholder={config.placeholder}
            disabled={sending}
            className="flex-1 resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-cyan-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-700 text-white shadow-sm hover:bg-cyan-800 disabled:bg-slate-300"
            title="Send (Enter)"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
